import { useState, useEffect, useMemo, useCallback } from "react";
import { Flex, Text, Box, useColorModeValue } from "@chakra-ui/react";

// local component
import { WalletStatus } from "components/ConnectWalletDropdown/ConnectWalletDropdown";
import { WalletActionBtn } from "components/WalletActionBtn/WalletActionBtn";
import TimerModal from "components/TimerModal";

// common components
import {
  Alert,
  InputAmount,
  ModalWrapper,
  TransactionResult,
  TxStatusModalBody,
} from "ui";

// utils
import { nativeTokenFormatter, transactionResultMessage } from "utils";

// hooks
import {
  useTransactionFee,
  useExchangeRate,
  useHashConnect,
  useAccount,
} from "hooks";

import {
  FIREBASE_EVENT_PROPERTIES,
  NATIVE_TOKEN_LABEL,
  NATIVE_TOKEN_MULTIPLIER,
  precision,
} from "constants/constants";
import { config } from "config/config";

// images
import Image from "next/image";
import hederaLight from "assets/hederaLight.svg";
import hederaDark from "assets/hederaDark.svg";
import hbarxLight from "assets/hbarxLight.svg";
import hbarxDark from "assets/hbarxDark.svg";

//types
import { TransactionCResponse } from "types/TransactionCResponse";

// third party
import { debounce } from "lodash";

// firebase analytics
import { firebaseEvents } from "utils/firebase";

const Stake = () => {
  const {
    status,
    stake,
    transactionStatus,
    transactionType,
    transactionId,
    showWalletModal,
    setTransactionId,
    selectedAccount,
    walletData,
    maintainanceData,
  } = useHashConnect();
  const { minDeposit, maxDeposit, coinName, coinNameX } = config;
  const { hbar, hbarX } = useAccount();

  const [amount, setAmount] = useState<number | undefined>();
  const [pastStakedAmount, setPastStakedAmount] = useState<number | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [transactionModal, showTransactionModal] = useState<boolean>(false);
  const [transactionResultModal, showTransactionResultModal] =
    useState<boolean>(false);
  const [underMaintainanceModal, showUnderMaintainanceModal] =
    useState<boolean>(false);
  const [transactionResult, setTransactionResult] = useState<string>("");
  // const [transactionResultId, setTransactionResultId] = useState<string>("");
  const { fee: transactionFee } = useTransactionFee();
  const { exchangeRate } = useExchangeRate();
  const hederaIcon = useColorModeValue(hederaLight, hederaDark);
  const hbarxIcon = useColorModeValue(hbarxLight, hbarxDark);

  // const handleTxView = (txId: string) => {
  //   const url = getTransactionURL(txId as string);
  //   window.open(url, "_blank");
  // };

  useEffect(() => {
    if (maintainanceData?.isActive) {
      showUnderMaintainanceModal(true);
    }
  }, [maintainanceData]);

  const showMaxButton = useMemo(() => {
    return status === "WALLET_CONNECTED";
  }, [status]);

  const isValidAmount = useCallback(
    (amount: number) => {
      if (!amount) {
        if (amount <= 0) {
          setAmountError(
            "This field can't be blank or zero. Please provide valid input."
          );
          return true;
        }
        amountError && setAmountError(null);
        return false;
      }
      const amt = amount * 10 ** 8;

      if (amt + transactionFee > hbar) {
        setAmountError("You do not have enough HBAR");
        return false;
      }

      if (amt < minDeposit) {
        setAmountError(
          `Entered amount should be equal or more than ${nativeTokenFormatter(
            minDeposit
          )} ${NATIVE_TOKEN_LABEL}`
        );
        return false;
      }
      if (amt > maxDeposit) {
        setAmountError(
          `Entered amount should be less than or equal to ${nativeTokenFormatter(
            maxDeposit
          )} ${NATIVE_TOKEN_LABEL}`
        );
        return false;
      }
      amountError && setAmountError(null);
      return true;
    },
    [amountError, hbar, maxDeposit, minDeposit, transactionFee]
  );

  useEffect(() => {
    if (amount !== undefined && hbar > 0 && amount > 0) {
      isValidAmount(amount);
    }
  }, [amount, hbar, isValidAmount]);

  useEffect(() => {
    if (
      transactionStatus === "START" &&
      transactionType === "stake" &&
      transactionId !== null
    ) {
      !transactionModal && showTransactionModal(true);
    } else {
      transactionModal && showTransactionModal(false);
    }
  }, [transactionStatus, transactionType, transactionId, transactionModal]);

  // const limitValue = (text: string) => {
  //   const limitDecimal = /^\d+(\.\d{0,4})?$/;
  //   if (text === "") return;
  //   else if (limitDecimal.test(text)) return parseFloat(text);
  //   return amount;
  // };

  const handleMaxClick = () => {
    let maxAmount = nativeTokenFormatter(hbar - transactionFee);
    if (maxAmount * 10 ** 8 > maxDeposit) {
      maxAmount = nativeTokenFormatter(maxDeposit);
    }
    if (maxAmount <= 0) {
      maxAmount = 0;
    }
    setAmount(maxAmount);
    isValidAmount(maxAmount);
    sendLog("max_stake");
  };

  // const updateAmount = (amount?: number) => {
  //   if (amount === undefined) {
  //     setAmount(undefined);
  //     amountError && setAmountError(null);
  //     return;
  //   }
  //   let decimalamt = limitValue(amount);
  //   setAmount(decimalamt);
  //   isValidAmount(decimalamt as number);
  // };

  const closeTransactionResultModal = () => {
    showTransactionResultModal(false);
    setTransactionResult("");
    // setTransactionResultId("");
  };

  const stakeHbar = async () => {
    if (amount) {
      sendLog("stake_cta");
      const {
        status,
      }: // transactionId
      TransactionCResponse = await stake(amount);
      if (status === "UNDER_MAINTAINANCE") {
        showUnderMaintainanceModal(true);
        return;
      }
      setPastStakedAmount(amount);
      setTransactionResult(status);
      // setTransactionResultId(transactionId);
      showTransactionResultModal(true);
      sendLog("stake_successful ");
    }
  };

  const sendLog = (name: string) => {
    const coinx = (hbarX / NATIVE_TOKEN_MULTIPLIER).toFixed(precision);
    const coin = (hbar / NATIVE_TOKEN_MULTIPLIER).toFixed(precision);
    const maxstake = amount;
    let firebaseRequest: any = {
      [FIREBASE_EVENT_PROPERTIES.EVENT_NAME]: name,
      [FIREBASE_EVENT_PROPERTIES.WALLET_NAME]:
        walletData.pairedWalletData?.name,
      [FIREBASE_EVENT_PROPERTIES.WALLET_ADDRESS]: selectedAccount,
    };
    if (name !== "Connect_Wallet") {
      firebaseRequest[FIREBASE_EVENT_PROPERTIES.TOKEN_BALANCE] = coin;
      firebaseRequest[FIREBASE_EVENT_PROPERTIES.LIQUIDTOKEN_BALANCE] = coinx;
      firebaseRequest[FIREBASE_EVENT_PROPERTIES.STAKE_AMOUNT_ENTERED] =
        maxstake;
    }
    firebaseEvents(firebaseRequest);
  };
  const youWillGetValue = useMemo(() => {
    if (amount !== undefined && exchangeRate !== undefined) {
      return parseFloat(
        ((Number(amount) * exchangeRate) / NATIVE_TOKEN_MULTIPLIER).toFixed(
          precision
        )
      );
    }
    return "-";
  }, [amount, exchangeRate]);

  const stakeHbardeb = debounce(stakeHbar, 1000);

  return (
    <>
      <Flex justifyContent="space-between" flexWrap="wrap">
        <Flex justifyContent="space-between" flexGrow={1}>
          <Box>
            <Text as="span" fontSize="14px">
              My {coinName} :{" "}
            </Text>
            <Text as="span" fontWeight="600" color="brand.textColor">
              {status === WalletStatus.WALLET_NOT_CONNECTED ? " -" : null}
              {status === WalletStatus.WALLET_CONNECTED
                ? ` 
                ${(hbar / NATIVE_TOKEN_MULTIPLIER).toFixed(precision)}`
                : null}
            </Text>
          </Box>
          <Box>
            <Text as="span" fontSize="14px">
              My {coinNameX} :{" "}
            </Text>
            <Text as="span" fontWeight="600" color="brand.textColor">
              {status === WalletStatus.WALLET_NOT_CONNECTED ? " -" : null}
              {status === WalletStatus.WALLET_CONNECTED
                ? ` ${(hbarX / NATIVE_TOKEN_MULTIPLIER).toFixed(precision)}`
                : null}
            </Text>
          </Box>
        </Flex>

        <InputAmount
          icon={hederaIcon}
          coinName={coinName}
          setAmount={setAmount}
          amount={amount}
          handleMaxClick={handleMaxClick}
          error={amountError}
          showMaxButton={showMaxButton}
          showError={status === WalletStatus.WALLET_CONNECTED}
        />

        <Flex justifyContent="space-between" flexGrow="1">
          <Flex alignItems="center">
            <Text as="span" fontSize="14px" mr="0.5rem">
              You will get :
            </Text>
            <Image src={hbarxIcon} width="28px" height="28px" alt="" />
            <Text
              as="span"
              fontSize="14px"
              fontWeight="600"
              color="brand.textColor"
              ml="0.5rem"
            >
              {youWillGetValue}
            </Text>{" "}
            <Text as="span" fontSize="14px" color="brand.textColor" ml="3px">
              {coinNameX}
            </Text>
          </Flex>
          <Flex alignItems={"center"}>
            <Text as="span" fontSize="14px">
              Transaction fees :
            </Text>
            <Text
              as="span"
              fontSize="14px"
              color="brand.textColor"
              fontWeight="600"
              pl="3px"
            >
              {`${nativeTokenFormatter(transactionFee)}`}
            </Text>
            <Text as="span" fontSize="14px" color="brand.textColor" ml="3px">
              {`${NATIVE_TOKEN_LABEL}`}
            </Text>
          </Flex>
        </Flex>

        <Box mt="32px" w="100%">
          <Alert
            type="error"
            message={amountError}
            showMessage={status === WalletStatus.WALLET_CONNECTED}
          />
        </Box>

        {status === WalletStatus.WALLET_NOT_CONNECTED && (
          <Alert
            type="info"
            message="Connect your wallet now to start staking"
            showMessage={true}
          />
        )}
        {status === WalletStatus.WALLET_CONNECTED ? (
          <WalletActionBtn
            text={`Stake ${coinName}`}
            onClick={() => stakeHbardeb()}
            disabled={amountError ? true : false}
          />
        ) : (
          <WalletActionBtn
            onClick={() => {
              sendLog("Connect_Wallet");
              showWalletModal(true);
            }}
            text="Connect Wallet"
            disabled={false}
          />
        )}
      </Flex>
      <ModalWrapper
        onClose={() => {
          showTransactionModal(false);
          setTransactionId(null);
        }}
        modal={transactionModal}
        body={<TxStatusModalBody transactionWebsite="Hashscan" />}
        header=""
        styles={{ height: "340px" }}
      ></ModalWrapper>

      <ModalWrapper
        onClose={closeTransactionResultModal}
        modal={transactionResultModal}
        body={
          <TransactionResult
            status={transactionResult}
            // openTransactionURL={() => handleTxView(transactionResultId)}
            closeTransactionModal={closeTransactionResultModal}
            message={transactionResultMessage({
              status: transactionResult,
              amount: pastStakedAmount,
              coin: config.coinName,
              transactionType: "staking",
            })}
          />
        }
        header=""
        styles={{ height: "340px" }}
      ></ModalWrapper>

      <ModalWrapper
        onClose={() => {
          showUnderMaintainanceModal(false);
        }}
        modal={underMaintainanceModal}
        body={<TimerModal />}
        header=""
        styles={{ height: "340px" }}
        showModalCloseButton={false}
        closeOnOverlayClick={false}
      ></ModalWrapper>
    </>
  );
};

export default Stake;
